/** Canonical Script Source context for `script_generation` nodes.
 *
 * Unifies GenerateCreative / creative_candidate / Text Asset upstream
 * inputs into ONE contract — Quick Action and manual edge share the same
 * normalization path (VISTOERA_GENERATE_SCRIPT_CANONICAL_SCRIPT_SOURCE_…). */

import type { Edge, Node } from '@xyflow/react'
import type { CreativeContextDragPayload, FlowNodeData } from './types'
import {
  buildCreativeContextField,
  computeCreativeContentFingerprint,
  type ScriptCreativeContextField,
  type ScriptSupportingAssetField,
} from './scriptGenerationCanvas'
import { CREATIVE_CONTEXT_EDGE_RELATION } from './scriptGenerationCanvas'

/** Reuses DERIVED_FROM — same provenance semantics as Creative→Script. */
export const SCRIPT_SOURCE_EDGE_RELATION = CREATIVE_CONTEXT_EDGE_RELATION

export const GENERATE_SCRIPT_SOURCE_INPUT_HANDLE = 'script-source-in'
export const GENERATE_SCRIPT_NODE_AGGREGATE_HANDLE = 'script-node-aggregate-in'

export const SCRIPT_INPUT_PORT_ATTR = 'data-script-input-port'

export type ScriptSourceKind = 'generate_creative' | 'creative_candidate' | 'text_asset'

export interface ScriptSourceContext {
  sourceNodeId: string
  sourceNodeType: ScriptSourceKind
  sourceKind: ScriptSourceKind
  /** Primary narrative text used for generation (never UI-truncated). */
  text: string
  title: string
  premise: string
  hook?: string
  tone?: string
  storyDirection?: string
  productRole?: string | null
  characterRole?: string | null
  estimatedDuration?: number | null
  tags?: string[]
  rationale?: string
  candidateId?: string
  sourceRunId?: string | null
  linkedAssetRefs?: ScriptSupportingAssetField[]
  linkedAt: string
  fingerprint: string
}

export function isScriptSourceInputHandle(handle: string | null | undefined): boolean {
  return handle === GENERATE_SCRIPT_SOURCE_INPUT_HANDLE || handle === GENERATE_SCRIPT_NODE_AGGREGATE_HANDLE
}

function titleFromTextAsset(text: string, label?: string | null): string {
  const fromLabel = String(label ?? '').trim()
  if (fromLabel) return fromLabel.slice(0, 120)
  const firstLine = text.split(/\r?\n/).map((l) => l.trim()).find(Boolean)
  if (firstLine) return firstLine.slice(0, 120)
  return '文本素材'
}

function fingerprintForScriptSource(ctx: Pick<ScriptSourceContext, 'sourceNodeId' | 'sourceKind' | 'text' | 'title' | 'premise' | 'tags'>): string {
  return JSON.stringify({
    sourceNodeId: ctx.sourceNodeId,
    sourceKind: ctx.sourceKind,
    text: ctx.text,
    title: ctx.title,
    premise: ctx.premise,
    tags: Array.isArray(ctx.tags) ? [...ctx.tags].sort() : [],
  })
}

/** Map legacy `creativeContext` → canonical `scriptSourceContext` on read. */
export function hydrateScriptSourceFromFields(fields: Record<string, unknown>): ScriptSourceContext | null {
  const existing = fields.scriptSourceContext as ScriptSourceContext | null | undefined
  if (existing?.sourceNodeId && existing.text) return existing
  const legacy = fields.creativeContext as ScriptCreativeContextField | null | undefined
  if (!legacy?.sourceNodeId) return null
  const kind: ScriptSourceKind =
    legacy.sourceNodeId && fields.creativeContext
      ? ((fields as { _scriptSourceKind?: ScriptSourceKind })._scriptSourceKind as ScriptSourceKind) ||
        'generate_creative'
      : 'generate_creative'
  const premise = String(legacy.premise ?? '').trim()
  const text = premise || String(legacy.hook ?? '').trim() || String(legacy.title ?? '').trim()
  return {
    sourceNodeId: legacy.sourceNodeId,
    sourceNodeType: kind,
    sourceKind: kind,
    text,
    title: String(legacy.title ?? '').trim() || titleFromTextAsset(text),
    premise: premise || text,
    hook: legacy.hook,
    tone: legacy.tone,
    storyDirection: legacy.storyDirection,
    productRole: legacy.productRole ?? null,
    characterRole: legacy.characterRole ?? null,
    estimatedDuration: legacy.estimatedDuration ?? null,
    tags: legacy.tags,
    rationale: legacy.rationale,
    candidateId: legacy.candidateId,
    sourceRunId: legacy.sourceRunId ?? null,
    linkedAssetRefs: legacy.linkedAssetRefs,
    linkedAt: legacy.linkedAt,
    fingerprint: legacy.fingerprint,
  }
}

export function resolveScriptSourceContext(fields: Record<string, unknown>): ScriptSourceContext | null {
  return hydrateScriptSourceFromFields(fields)
}

export function buildScriptSourceFromCreative(
  payload: CreativeContextDragPayload,
  briefAssets: Array<Record<string, unknown>>,
  sourceKind: ScriptSourceKind = 'generate_creative',
): { scriptSource: ScriptSourceContext; creativeContext: ScriptCreativeContextField } {
  const creativeContext = buildCreativeContextField(payload, briefAssets)
  const premise = String(creativeContext.premise ?? '').trim()
  const text = premise || String(creativeContext.hook ?? '').trim() || String(creativeContext.title ?? '').trim()
  const scriptSource: ScriptSourceContext = {
    sourceNodeId: creativeContext.sourceNodeId,
    sourceNodeType: sourceKind,
    sourceKind,
    text,
    title: String(creativeContext.title ?? '').trim() || titleFromTextAsset(text),
    premise: premise || text,
    hook: creativeContext.hook,
    tone: creativeContext.tone,
    storyDirection: creativeContext.storyDirection,
    productRole: creativeContext.productRole ?? null,
    characterRole: creativeContext.characterRole ?? null,
    estimatedDuration: creativeContext.estimatedDuration ?? null,
    tags: creativeContext.tags,
    rationale: creativeContext.rationale,
    candidateId: creativeContext.candidateId,
    sourceRunId: creativeContext.sourceRunId ?? null,
    linkedAssetRefs: creativeContext.linkedAssetRefs,
    linkedAt: creativeContext.linkedAt,
    fingerprint: creativeContext.fingerprint || fingerprintForScriptSource({
      sourceNodeId: creativeContext.sourceNodeId,
      sourceKind,
      text,
      title: creativeContext.title,
      premise: premise || text,
      tags: creativeContext.tags,
    }),
  }
  return { scriptSource, creativeContext }
}

export function buildScriptSourceFromTextAsset(source: Node): ScriptSourceContext | null {
  const data = source.data as unknown as FlowNodeData
  if (data.nodeType !== 'text_asset') return null
  const fields = (data.fields || {}) as Record<string, unknown>
  const text = String(fields.text ?? '').trim()
  if (!text) return null
  const title = titleFromTextAsset(text, typeof fields.label === 'string' ? fields.label : null)
  const sourceKind: ScriptSourceKind = 'text_asset'
  return {
    sourceNodeId: source.id,
    sourceNodeType: sourceKind,
    sourceKind,
    text,
    title,
    premise: text,
    linkedAt: new Date().toISOString(),
    fingerprint: fingerprintForScriptSource({
      sourceNodeId: source.id,
      sourceKind,
      text,
      title,
      premise: text,
    }),
  }
}

export function isScriptSourceInputReady(ctx: ScriptSourceContext | null | undefined): boolean {
  if (!ctx) return false
  const title = String(ctx.title ?? '').trim()
  const premise = String(ctx.premise ?? ctx.text ?? '').trim()
  return title.length > 0 && premise.length > 0
}

export function isTextAssetReadyForScriptLink(source: Node): boolean {
  return buildScriptSourceFromTextAsset(source) != null
}

/** Live fingerprint for upstream text_asset edits (stale detection). */
export function liveTextAssetScriptSourceFingerprint(nodes: import('@xyflow/react').Node[], sourceNodeId: string): string | undefined {
  const source = nodes.find((n) => n.id === sourceNodeId)
  if (!source) return undefined
  const ctx = buildScriptSourceFromTextAsset(source)
  return ctx?.fingerprint
}

export function scriptSourcePreviewText(ctx: ScriptSourceContext, maxLen = 160): string {
  const raw = String(ctx.premise ?? ctx.text ?? '').trim()
  if (raw.length <= maxLen) return raw
  return `${raw.slice(0, maxLen)}…`
}

export function scriptSourceSourceLabel(ctx: ScriptSourceContext, t?: (key: string) => string): string {
  if (ctx.sourceKind === 'text_asset') return t ? t('scriptGeneration.scriptSourceFromTextAsset') : '文字 / 文本素材'
  if (ctx.sourceKind === 'creative_candidate') return t ? t('scriptGeneration.scriptSourceFromCreativeCandidate') : '创意候选'
  return t ? t('scriptGeneration.scriptSourceFromCreative') : '生成创意'
}

/** Clear edge-derived script source; preserve user-owned local edits elsewhere. */
export function clearScriptSourceOnUnlink(
  fields: Record<string, unknown>,
  sourceNodeId: string | null | undefined,
): Record<string, unknown> {
  const ctx = resolveScriptSourceContext(fields)
  if (!ctx) return fields
  if (sourceNodeId && ctx.sourceNodeId !== sourceNodeId) return fields
  const existing = Array.isArray(fields.supportingAssets)
    ? (fields.supportingAssets as ScriptSupportingAssetField[])
    : []
  const nextAssets = existing.filter((a) => {
    const isInherited = a.inherited === true || a.source === 'inherited'
    if (!isInherited) return true
    if (sourceNodeId && a.sourceNodeId) return a.sourceNodeId !== sourceNodeId
    return false
  })
  return {
    ...fields,
    scriptSourceContext: null,
    creativeContext: null,
    supportingAssets: nextAssets,
  }
}

export function mergeSupportingAssetsOnScriptSourceLink(
  existing: ScriptSupportingAssetField[],
  newlyInherited: ScriptSupportingAssetField[],
  previousSourceNodeId: string | null | undefined,
): ScriptSupportingAssetField[] {
  const kept = existing.filter((a) => {
    const isInherited = a.inherited === true || a.source === 'inherited'
    if (!isInherited) return true
    if (previousSourceNodeId && a.sourceNodeId && a.sourceNodeId === previousSourceNodeId) return false
    if (previousSourceNodeId && !a.sourceNodeId) return false
    return true
  })
  const keys = new Set(kept.map((a) => a.asset_id || `${a.asset_type}:${a.display_name}`))
  const additions = newlyInherited.filter((a) => !keys.has(a.asset_id || `${a.asset_type}:${a.display_name}`))
  return [...kept, ...additions]
}

export function patchScriptSourceFields(
  scriptFields: Record<string, unknown>,
  scriptSource: ScriptSourceContext,
  supportingAssets: ScriptSupportingAssetField[],
): Record<string, unknown> {
  const creativeContext: ScriptCreativeContextField = {
    sourceNodeId: scriptSource.sourceNodeId,
    sourceRunId: scriptSource.sourceRunId ?? null,
    assetsSourceNodeId: scriptSource.sourceNodeId,
    candidateId: scriptSource.candidateId || scriptSource.sourceNodeId,
    title: scriptSource.title,
    premise: scriptSource.premise,
    hook: scriptSource.hook ?? '',
    tone: scriptSource.tone ?? '',
    storyDirection: scriptSource.storyDirection ?? '',
    productRole: scriptSource.productRole ?? null,
    characterRole: scriptSource.characterRole ?? null,
    estimatedDuration: scriptSource.estimatedDuration ?? null,
    tags: scriptSource.tags ?? [],
    rationale: scriptSource.rationale ?? '',
    linkedAssetRefs: scriptSource.linkedAssetRefs ?? [],
    linkedAt: scriptSource.linkedAt,
    fingerprint: scriptSource.fingerprint,
  }
  return {
    ...scriptFields,
    scriptSourceContext: scriptSource,
    creativeContext,
    supportingAssets,
  }
}

export function computeLiveScriptSourceFingerprint(
  nodes: Node[],
  scriptSource: ScriptSourceContext | null,
): string | undefined {
  if (!scriptSource) return undefined
  const source = nodes.find((n) => n.id === scriptSource.sourceNodeId)
  if (!source) return undefined
  if (scriptSource.sourceKind === 'text_asset') {
    return buildScriptSourceFromTextAsset(source)?.fingerprint
  }
  const data = source.data as unknown as FlowNodeData
  const fields = (data.fields || {}) as Record<string, unknown>
  const currentResult = fields.currentResult as Record<string, unknown> | null | undefined
  if (currentResult) {
    return computeCreativeContentFingerprint({
      title: String(currentResult.title ?? ''),
      premise: String(currentResult.premise ?? ''),
      hook: String(currentResult.hook ?? ''),
      tone: String(currentResult.tone ?? ''),
      storyDirection: String(currentResult.storyDirection ?? ''),
      productRole: (currentResult.productRole as string | null) ?? null,
      characterRole: (currentResult.characterRole as string | null) ?? null,
      estimatedDuration: (currentResult.estimatedDuration as number | null) ?? null,
      tags: Array.isArray(currentResult.tags) ? (currentResult.tags as string[]) : [],
      rationale: String(currentResult.rationale ?? ''),
    })
  }
  return scriptSource.fingerprint
}

export function findCreativeContextSourceNode(nodes: Node[], edges: Edge[], scriptNodeId: string): Node | null {
  const edge = edges.find((e) => {
    if (e.target !== scriptNodeId) return false
    const relation = (e.data as { relationType?: string } | undefined)?.relationType
    return relation === CREATIVE_CONTEXT_EDGE_RELATION || relation === SCRIPT_SOURCE_EDGE_RELATION || relation === 'INPUT_TO'
  })
  if (!edge) return null
  const source = nodes.find((n) => n.id === edge.source) ?? null
  if (!source) return null
  const st = (source.data as unknown as FlowNodeData).nodeType
  if (st === 'generate_creative' || st === 'creative_candidate' || st === 'text_asset') return source
  return null
}

export function liveCreativeContextFingerprint(nodes: Node[], _edges: Edge[], scriptNodeId: string): string | undefined {
  const scriptNode = nodes.find((n) => n.id === scriptNodeId)
  const scriptFields = ((scriptNode?.data as unknown as FlowNodeData | undefined)?.fields || {}) as Record<string, unknown>
  const ctx = resolveScriptSourceContext(scriptFields)
  return computeLiveScriptSourceFingerprint(nodes, ctx)
}

export function buildScriptSourceEdgeFields(opts: {
  sourceNodeId: string
  targetNodeId: string
  sourceHandle?: string | null
}): {
  source: string
  target: string
  sourceHandle?: string
  targetHandle: string
  data: { relationType: string }
  className: string
} {
  return {
    source: opts.sourceNodeId,
    target: opts.targetNodeId,
    sourceHandle: opts.sourceHandle ?? undefined,
    targetHandle: GENERATE_SCRIPT_SOURCE_INPUT_HANDLE,
    data: { relationType: SCRIPT_SOURCE_EDGE_RELATION },
    className: 'pcv-edge',
  }
}
